import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Clock, ArrowRight, Check, Handshake, Trophy, Sparkles } from "lucide-react";
import { PageHero, SectionLabel } from "@/components/Section";
import { EVENT, IMAGES, DAY_INCLUDES, SCHEDULE } from "@/data/content";

export default function EventDetails() {
  return (
    <div data-testid="event-page">
      <PageHero
        overline={`${EVENT.edition} · ${EVENT.location}`}
        title="Event"
        accent="Details"
        subtitle={`${EVENT.dateLabel} · ${EVENT.dayLabel} — a four-person scramble at The Woodlands Country Club — The Trails in Spring, Texas.`}
        image={IMAGES.golferSwing}
      />

      {/* Quick facts */}
      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
          {[
            { icon: Calendar, label: "When", main: EVENT.dateLabel, sub: EVENT.dayLabel },
            { icon: MapPin, label: "Where", main: EVENT.venue, sub: EVENT.venueNote },
            { icon: Users, label: "Format", main: EVENT.teams, sub: EVENT.teamsSub },
          ].map((s) => (
            <div key={s.label} className="border border-border bg-background p-7 hard-shadow-sm">
              <div className="flex items-center gap-2 text-rust"><s.icon size={16} /><span className="label-font text-xs">{s.label}</span></div>
              <div className="mt-3 font-serif font-bold text-2xl text-espresso">{s.main}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* The Experience */}
      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <SectionLabel>The Experience</SectionLabel>
              <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso leading-[1.05]">
                More Than a <span className="text-rust">Round of Golf</span>
              </h2>
              <div className="mt-6 space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
                <p>
                  From the first tee shot to the final toast, the Heroes &amp; Legacy Golf Classic is a full day of energy, heart, and connection. It's four-person scramble golf at its most fun — friendly rivalries, on-course surprises, and plenty of bragging rights up for grabs.
                </p>
                <p>
                  More than that, it's a rare chance to stand shoulder-to-shoulder with the firefighters, officers, medics, and dispatchers who serve our community — alongside business owners, executives, and community leaders who show up to give back. Real conversations, new friendships, and shared purpose happen here.
                </p>
                <p>
                  Come for the competition. Stay for the camaraderie. Leave with memories — and a story — that last long after the final putt drops.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <img src={IMAGES.ffCrew} alt="Firefighter crew together" className="w-full h-64 object-cover border border-border hard-shadow-sm" />
              <img src={IMAGES.golferSwing} alt="Golfer taking a swing" className="w-full h-64 object-cover border border-border hard-shadow-sm mt-8" />
            </div>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Handshake, title: "Connection & Camaraderie", desc: "Meet and connect with first responders and community leaders in an authentic, relaxed setting built for real conversation." },
              { icon: Trophy, title: "Friendly Competition", desc: "A lively four-person scramble with on-course contests, team rivalries, and awards — bring your A-game and your sense of humor." },
              { icon: Sparkles, title: "Lasting Memories", desc: "Great food, good company, and a celebration of service that turns a day on the course into memories you'll carry for years." },
            ].map((c) => (
              <div key={c.title} className="border border-border bg-card p-7 transition-transform hover:-translate-y-1 hard-shadow-sm">
                <span className="h-12 w-12 bg-rust text-primary-foreground flex items-center justify-center"><c.icon size={22} /></span>
                <h3 className="mt-5 font-serif font-bold text-xl text-espresso">{c.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="paper-noise bg-background">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>Day Of</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Schedule Of Play</h2>
          <div className="mt-12 space-y-0 border-l-2 border-rust ml-3">
            {SCHEDULE.map((s) => (
              <div key={s.time} className="relative pl-8 pb-10 last:pb-0">
                <span className="absolute -left-[9px] top-1 h-4 w-4 bg-rust border-2 border-background" />
                <div className="flex items-center gap-2 text-rust label-font text-xs"><Clock size={13} /> {s.time}</div>
                <h3 className="mt-1 font-serif font-bold text-2xl text-espresso">{s.title}</h3>
                <p className="mt-1 text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Includes */}
      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-6 border border-border hard-shadow overflow-hidden">
            <img src={IMAGES.trophies} alt="Tournament trophies" className="w-full h-96 object-cover" />
          </div>
          <div className="lg:col-span-6">
            <SectionLabel>Included For Every Player</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl text-espresso">The Day Includes</h2>
            <ul className="mt-8 space-y-4">
              {DAY_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-espresso">
                  <span className="shrink-0 mt-0.5 h-6 w-6 bg-rust text-primary-foreground flex items-center justify-center"><Check size={14} /></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-espresso text-cream">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-serif font-black text-3xl sm:text-4xl">Ready To Play?</h2>
          <p className="mt-4 text-cream/80">Limited to {EVENT.teams.toLowerCase()} — secure your spot today.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow flex items-center gap-2">Register a Team <ArrowRight size={16} /></Link>
            <Link to="/sponsor" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">Become a Sponsor</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
